import { itx } from "../../../src/translationItx/itx";
import { loadPdfDocumentHeader, type PdfDocumentHeader } from "../../../src/pdf/PdfDocumentHeader";
import { protectPdfOutput } from "../../../src/pdf/protectPdfOutput";
import { jsPDF } from "jspdf";
import "svg2pdf.js";
import { PDFDocument } from "pdf-lib";
import { C1_PdfImageOptimizer } from "../lib/C1_PdfImages";
import { deduplicateC1PdfImages } from "../lib/C1_PdfImageDeduplication";
import { C1_DOCUMENT_LOGO, C1_PRODUCT_NAME, ESTADA_PDF_COVER_LOGO } from "../C1_Const";
import { getC1DocumentationMarkdown, createC1MarkdownCoverPdfBlob } from "../raumbuch/C1_RaumbuchCoverMarkdown";
import { addC1PdfOutlines, addC1PdfPageLinks } from "../lib/C1_PdfOutlines";
import { createC1PdfTableOfContentsBlob } from "../lib/C1_PdfTableOfContents";
import type { C1_PdfChapterArtifact } from "../lib/C1_PdfChapter";
import { registerC1PdfFonts } from "../lib/C1_PdfFonts";
import { applyInlineEloGraphicContrast } from "./C1_EloGraphicContrast";
import type { C1_Floor } from "../model/C1_Floor";
import type { C1_Project } from "../model/C1_Project";
import type { C1_Room } from "../model/C1_Room";
import type { Rect } from "../types/C1_AppTypes";
import { isSpecialExteriorRoomType, type WallSide } from "../types/C1_ModelTypes";
import { hasContinuousExteriorWalls } from "../types/C1_RoomTypes";
import { c1PdfFileName } from "../lib/C1_PdfFileName";
import { roomNeighborContacts, roomNeighborLabelDistance } from "./C1_roomNeighborContacts";
import { roomPlanOpenings } from "./C1_roomPlanOpenings";

export type C1_RoomPlanPdfDiagram = {
  id: string;
  floorLabel: string;
  floorName?: string;
  roomLabel: string;
  markup: string;
  roomIds: string[];
  openingIds: string[];
  crop: { x: number; y: number; width: number; height: number };
  neighborContext: Array<{
    id: string;
    name: string;
    label: {
      x: number;
      y: number;
      side: "left" | "right" | "top" | "bottom";
      distance: number;
    };
    walls: Array<{
      x: number;
      y: number;
      width: number;
      height: number;
      outline: string;
      cutSide: "left" | "right" | "top" | "bottom";
    }>;
  }>;
};

export type C1_RoomPlanPdfGroup = {
  name: string;
  floorLabel: string;
  diagrams: C1_RoomPlanPdfDiagram[];
};

export function createC1RoomPlanNeighborContext({
  floor,
  rooms,
  project,
  view,
  px,
}: {
  floor: C1_Floor;
  rooms: C1_Room[];
  project: C1_Project;
  view: Rect;
  px: number;
}): C1_RoomPlanPdfDiagram["neighborContext"] {
  const roomIds = new Set(rooms.map((room) => room.id));
  const tolerance = 0.001;
  const contextDepth = 0.8;
  const openings = roomPlanOpenings(floor, roomIds);
  const toSvgX = (value: number) => (value - view.x1) * px;
  const toSvgY = (value: number) => (value - view.y1) * px;

  return floor.rooms
    .filter((candidate) => !roomIds.has(candidate.id))
    .flatMap((candidate) => {
      const candidateRect = candidate.rect();
      const contacts = roomNeighborContacts(candidate, rooms);
      if (!contacts.length) return [];

      const contact = contacts.sort((left, right) => right.end - right.start - (left.end - left.start))[0];
      const middle = (contact.start + contact.end) / 2;
      const labelDistance = roomNeighborLabelDistance(contact, openings, (candidate.owner?.name ?? ""), px, project.settings.wallThicknessInterior);
      const horizontalNeighbor = contact.side === "left" || contact.side === "right";
      const direction = contact.side === "left" || contact.side === "top" ? -1 : 1;
      const availableDepth = horizontalNeighbor
        ? contact.side === "left"
          ? contact.boundary - candidateRect.x1
          : candidateRect.x2 - contact.boundary
        : contact.side === "top"
          ? contact.boundary - candidateRect.y1
          : candidateRect.y2 - contact.boundary;
      const depth = Math.min(contextDepth, Math.max(0, availableDepth));
      const contextWall = (side: WallSide, position: number) => {
        const specialRoomFallback = isSpecialExteriorRoomType(candidate.type) && !hasContinuousExteriorWalls(candidate.type);
        const sourceRoom = specialRoomFallback ? contact.targetRoom : candidate;
        const sourceSide = specialRoomFallback ? contact.side : side;
        const sourcePosition = specialRoomFallback ? middle : contact.boundary;
        if (sourceRoom.isWallHidden(sourceSide, project, sourcePosition)) return undefined;
        const segment = sourceRoom
          .wallSegments(sourceSide)
          .find((item) => sourcePosition >= item.start - tolerance && sourcePosition <= item.end + tolerance);
        if (!segment) return undefined;
        const thicknessModel = segment.exterior
          ? project.settings.wallThicknessExterior
          : project.settings.wallThicknessInterior;
        const thickness = thicknessModel * px;
        const outline = segment.exterior ? project.settings.wallColorExterior : project.settings.wallColorInterior;
        const end = contact.boundary + direction * depth;
        return horizontalNeighbor
          ? {
              x: toSvgX(Math.min(contact.boundary, end)),
              y: toSvgY(position) - thickness / 2,
              width: depth * px,
              height: thickness,
              outline,
              cutSide: contact.side,
            }
          : {
              x: toSvgX(position) - thickness / 2,
              y: toSvgY(Math.min(contact.boundary, end)),
              width: thickness,
              height: depth * px,
              outline,
              cutSide: contact.side,
            };
      };
      const walls = (
        horizontalNeighbor
          ? [
              Math.abs(contact.start - candidateRect.y1) <= tolerance
                ? contextWall("top", candidateRect.y1)
                : undefined,
              Math.abs(contact.end - candidateRect.y2) <= tolerance
                ? contextWall("bottom", candidateRect.y2)
                : undefined,
            ]
          : [
              Math.abs(contact.start - candidateRect.x1) <= tolerance
                ? contextWall("left", candidateRect.x1)
                : undefined,
              Math.abs(contact.end - candidateRect.x2) <= tolerance
                ? contextWall("right", candidateRect.x2)
                : undefined,
            ]
      ).filter((wall): wall is NonNullable<typeof wall> => wall !== undefined);

      return [
        {
          id: candidate.id,
          name: (candidate.owner?.name ?? ""),
          label: horizontalNeighbor
            ? { x: toSvgX(contact.boundary), y: toSvgY(middle), side: contact.side, distance: labelDistance * px }
            : { x: toSvgX(middle), y: toSvgY(contact.boundary), side: contact.side, distance: labelDistance * px },
          walls,
        },
      ];
    });
}

const neighborLabelFontSize = 12;

type C1_RoomPlanPdfOptions = {
  projectName: string;
  dateText: string;
  groups: C1_RoomPlanPdfGroup[];
  showTags?: boolean;
};

const margin = 45;
const contentTop = 86;
const footerTopOffset = 48;
const lineColor = "#aeb9bf";
const mutedColor = "#586a75";

const diagramRenderTimeoutMs = 60_000;

/** requestAnimationFrame pauses in background tabs; the timer keeps the export running there. */
function yieldToBrowser() {
  return new Promise<void>((resolve) => {
    const timer = window.setTimeout(resolve, 50);
    window.requestAnimationFrame(() => {
      window.clearTimeout(timer);
      resolve();
    });
  });
}

/** Turns a stalled asynchronous render step into an error that identifies the affected room. */
function withDiagramTimeout<T>(promise: Promise<T>, diagram: C1_RoomPlanPdfDiagram): Promise<T> {
  let timer: number | undefined;
  const timeout = new Promise<never>((_, reject) => {
    timer = window.setTimeout(
      () => reject(new Error(itx(
        "c1.ui.Raumgrundriss_value1_konnte_nicht_gezeichnet_werden_Zeituberschreitung",
        "Raumgrundriss „{{value1}}“ konnte nicht gezeichnet werden (Zeitüberschreitung).",
        { "{{value1}}": `${diagram.floorLabel} · ${diagram.roomLabel}` },
      ))),
      diagramRenderTimeoutMs,
    );
  });
  return Promise.race([promise, timeout]).finally(() => window.clearTimeout(timer));
}



/** Keeps room entries identical in page headers, contents and PDF bookmarks. */
export function roomPlanGroupTitle(group: C1_RoomPlanPdfGroup, showTags: boolean): string {
  if (!showTags) return group.name;
  return Array.from(new Set(group.diagrams.map(diagram => diagram.roomLabel))).join(" \u00b7 ") || group.name;
}

/** Resolves displayed room and floor tags before drawing the shared header. */
function drawHeader(document: jsPDF, group: C1_RoomPlanPdfGroup, header: PdfDocumentHeader, showTags: boolean) {
  const floorLabels = group.diagrams.map(diagram => showTags ? diagram.floorLabel : (diagram.floorName ?? diagram.floorLabel));
  const floors = Array.from(new Set(floorLabels)).join(" \u00b7 ");
  const room = roomPlanGroupTitle(group, showTags);
  header.draw(document, { planType: itx("c1.ui.Raumgrundriss", "Raumgrundriss"), hierarchy: [floors, room] });
}

function drawFooter(document: jsPDF, options: C1_RoomPlanPdfOptions, page: number, totalPages: number) {
  const pageWidth = document.internal.pageSize.getWidth();
  const pageHeight = document.internal.pageSize.getHeight();
  const y = pageHeight - footerTopOffset;
  document.setDrawColor(lineColor);
  document.setLineWidth(1);
  document.line(margin, y, pageWidth - margin, y);
  document.setTextColor(mutedColor);
  document.setFont("helvetica", "normal");
  document.setFontSize(6.5);
  document.text(itx("c1.ui.Projekt_value1", "Projekt: {{value1}}", { "{{value1}}": String(options.projectName) }), margin, y + 14);
  document.text(itx("c1.ui.Stand_value1", "Stand: {{value1}}", { "{{value1}}": String(options.dateText) }), pageWidth / 2, y + 14, { align: "center" });
  document.text(`${page} / ${totalPages}`, pageWidth - margin, y + 14, { align: "right" });
}

function prepareSvgTextLayout(svg: SVGSVGElement) {
  const host = window.document.createElement("div");
  host.style.position = "fixed";
  host.style.left = "-100000px";
  host.style.top = "0";
  host.style.opacity = "0";
  host.style.pointerEvents = "none";
  host.appendChild(svg);
  window.document.body.appendChild(host);
  try {
    svg.querySelectorAll<SVGTextElement>('[data-c1-svg-layer="elo-items"] text').forEach((text) => {
      const intendedCenterY = Number.parseFloat(text.getAttribute("y") ?? "");
      if (!Number.isFinite(intendedCenterY)) return;
      text.removeAttribute("dominant-baseline");
      text.style.removeProperty("dominant-baseline");
      // Room PDFs calculate an explicit baseline below; do not apply SVG alignment a second time.
      text.removeAttribute("alignment-baseline");
      text.style.removeProperty("alignment-baseline");
      const box = text.getBBox();
      const actualCenterY = box.y + box.height / 2;
      text.setAttribute("y", String(intendedCenterY + intendedCenterY - actualCenterY));
    });
    svg.querySelectorAll<SVGTextElement>("[data-c1-neighbor-label]").forEach((text) => {
      const side = text.dataset.c1NeighborLabel;
      const distance = Number(text.dataset.c1NeighborDistance);
      const x = Number(text.getAttribute("x"));
      const y = Number(text.getAttribute("y"));
      const box = text.getBBox();
      if (side === "right") {
        const positionedX = x + distance - (box.y - y);
        text.setAttribute("x", String(positionedX));
        text.setAttribute("transform", `rotate(-90 ${positionedX} ${y})`);
      } else if (side === "left") {
        const positionedX = x - distance - (box.y + box.height - y);
        text.setAttribute("x", String(positionedX));
        text.setAttribute("transform", `rotate(-90 ${positionedX} ${y})`);
      } else if (side === "bottom") {
        text.setAttribute("y", String(y + distance - (box.y - y)));
      } else if (side === "top") {
        text.setAttribute("y", String(y - distance - (box.y + box.height - y)));
      }
      delete text.dataset.c1NeighborLabel;
      delete text.dataset.c1NeighborDistance;
    });
  } finally {
    host.remove();
  }
}

export function createC1RoomPlanSvg(diagram: C1_RoomPlanPdfDiagram): SVGSVGElement {
  const svg = new DOMParser().parseFromString(diagram.markup, "image/svg+xml")
    .documentElement as unknown as SVGSVGElement;
  applyInlineEloGraphicContrast(svg);
  const allowedLayers = new Set([
    "floor-finishes",
    "exterior-walls",
    "interior-walls",
    "free-walls",
    "openings",
    "furniture",
    "elo-items",
    "elo-debug",
    "north-arrow",
  ]);
  svg.querySelectorAll<SVGElement>("[data-c1-svg-layer]").forEach((layer) => {
    if (!allowedLayers.has(layer.dataset.c1SvgLayer ?? "")) layer.remove();
  });

  const roomIds = new Set(diagram.roomIds);
  for (const layerName of ["floor-finishes", "exterior-walls", "interior-walls", "furniture"]) {
    svg.querySelectorAll<SVGElement>(`[data-c1-svg-layer="${layerName}"] [data-c1-room-id]`).forEach((element) => {
      if (!roomIds.has(element.dataset.c1RoomId ?? "")) element.remove();
    });
  }

  const openingIds = new Set(diagram.openingIds);
  svg.querySelectorAll<SVGElement>('[data-c1-svg-layer="openings"] [data-c1-opening-id]').forEach((element) => {
    if (!openingIds.has(element.dataset.c1OpeningId ?? "")) element.remove();
  });

  svg.querySelectorAll<SVGElement>('[data-c1-svg-layer="elo-items"] > [data-c1-room-id]').forEach((element) => {
    const belongsToRoom = roomIds.has(element.dataset.c1RoomId ?? "");
    const belongsToOpening = openingIds.has(element.dataset.c1OpeningId ?? "");
    if (!belongsToRoom && !belongsToOpening) {
      element.remove();
      return;
    }

    if (!element.dataset.c1OpeningId) return;
    element.querySelectorAll<SVGElement>("[data-c1-definition-id]").forEach((eloItem) => {
      const definitionId = eloItem.dataset.c1DefinitionId;
      const outsideButton = eloItem.dataset.c1OpeningSide === "outside" || definitionId === "door-internal.button-outside" ||
        (definitionId?.startsWith("passage.") && definitionId.includes("-outside-"));
      if ((belongsToRoom && outsideButton) || (!belongsToRoom && !outsideButton)) eloItem.remove();
    });
  });

  const hiddenWalls = new Set<string>();
  svg.querySelectorAll<SVGLineElement>('[data-c1-hidden-wall="true"]').forEach((line) => {
    const partnerRoomIds = (line.dataset.c1PartnerRoomIds ?? "").split(",").filter(Boolean);
    if (partnerRoomIds.some((roomId) => roomIds.has(roomId))) {
      line.remove();
      return;
    }
    const first = `${line.getAttribute("x1")},${line.getAttribute("y1")}`;
    const second = `${line.getAttribute("x2")},${line.getAttribute("y2")}`;
    const key = [first, second].sort().join("|");
    if (hiddenWalls.has(key)) line.remove();
    else hiddenWalls.add(key);
  });

  svg
    .querySelectorAll(
      "foreignObject, .C1_svgOpeningHitArea, .C1_dimHit, .C1_svgButtonHoverPreview, .C1_svgLightHoverPreview, .C1_svgLightHitArea",
    )
    .forEach((element) => element.remove());

  if (diagram.neighborContext.length) {
    const namespace = "http://www.w3.org/2000/svg";
    const contextLayer = svg.ownerDocument.createElementNS(namespace, "g");
    contextLayer.setAttribute("data-c1-svg-layer", "room-plan-neighbor-context");
    contextLayer.setAttribute("pointer-events", "none");

    diagram.neighborContext.forEach((neighbor) => {
      neighbor.walls.forEach((wall) => {
        const wallGroup = svg.ownerDocument.createElementNS(namespace, "g");
        const vertical = wall.cutSide === "top" || wall.cutSide === "bottom";
        const length = vertical ? wall.height : wall.width;
        const thickness = vertical ? wall.width : wall.height;
        const transform =
          wall.cutSide === "right"
            ? `translate(${wall.x} ${wall.y})`
            : wall.cutSide === "left"
              ? `translate(${wall.x + wall.width} ${wall.y + wall.height}) rotate(180)`
              : wall.cutSide === "bottom"
                ? `translate(${wall.x + wall.width} ${wall.y}) rotate(90)`
                : `translate(${wall.x} ${wall.y + wall.height}) rotate(-90)`;
        wallGroup.setAttribute("transform", transform);

        const path = svg.ownerDocument.createElementNS(namespace, "path");
        const strokeWidth = 0.8;
        const inset = strokeWidth / 2;
        const left = inset;
        const right = length - inset;
        const top = inset;
        const bottom = thickness - inset;
        const middleY = (top + bottom) / 2;
        const waveDepth = Math.min(thickness * 0.24, 3);
        const quarterHeight = (bottom - top) / 4;
        const commands =
          `M ${left} ${top} L ${right} ${top} ` +
          `C ${right - waveDepth} ${top + quarterHeight * 0.5} ${right - waveDepth} ${middleY - quarterHeight * 0.5} ${right} ${middleY} ` +
          `C ${right + waveDepth} ${middleY + quarterHeight * 0.5} ${right + waveDepth} ${bottom - quarterHeight * 0.5} ${right} ${bottom} ` +
          `L ${left} ${bottom} Z`;
        path.setAttribute("d", commands);
        path.setAttribute("fill", "#f1f3f4");
        path.setAttribute("stroke", wall.outline);
        path.setAttribute("stroke-width", String(strokeWidth));
        path.setAttribute("stroke-linejoin", "miter");
        wallGroup.appendChild(path);
        contextLayer.appendChild(wallGroup);
      });
      const label = svg.ownerDocument.createElementNS(namespace, "text");
      label.setAttribute("x", String(neighbor.label.x));
      label.setAttribute("y", String(neighbor.label.y));
      label.setAttribute("fill", "#a8b2ba");
      label.setAttribute("font-family", "Inter");
      label.setAttribute("font-size", String(neighborLabelFontSize));
      label.setAttribute("font-style", "normal");
      label.setAttribute("font-weight", "400");
      label.setAttribute("text-anchor", "middle");
      label.setAttribute("dominant-baseline", "central");
      label.dataset.c1NeighborLabel = neighbor.label.side;
      label.dataset.c1NeighborDistance = String(neighbor.label.distance);
      label.textContent = neighbor.name;
      contextLayer.appendChild(label);
    });
    const firstRenderedLayer = svg.querySelector(":scope > [data-c1-svg-layer]");
    svg.insertBefore(contextLayer, firstRenderedLayer ?? null);
  }
  svg.querySelectorAll<SVGSVGElement>("svg").forEach((nestedSvg) => {
    for (const property of ["width", "height"] as const) {
      const styleValue = nestedSvg.style.getPropertyValue(property).trim();
      if (styleValue && !Number.isFinite(Number.parseFloat(styleValue))) nestedSvg.style.removeProperty(property);
    }
    nestedSvg.setAttribute("overflow", "visible");
  });
  svg.setAttribute("viewBox", `${diagram.crop.x} ${diagram.crop.y} ${diagram.crop.width} ${diagram.crop.height}`);
  svg.setAttribute("width", String(diagram.crop.width));
  svg.setAttribute("height", String(diagram.crop.height));
  svg.setAttribute("overflow", "visible");
  const northArrow = svg.querySelector<SVGGElement>('[data-c1-svg-layer="north-arrow"]');
  if (northArrow) {
    // The floor's viewport position lies outside most room crops.
    const size = 80;
    const margin = 16;
    const x = diagram.crop.x + diagram.crop.width - size - margin;
    const y = diagram.crop.y + margin;
    northArrow.setAttribute("transform", `translate(${x} ${y})`);
    northArrow.style.pointerEvents = "none";
  }
  prepareSvgTextLayout(svg);
  return svg;
}

export function c1RoomPlanSvgDataUrl(
  diagram: C1_RoomPlanPdfDiagram,
  renderedSize?: { width: number; height: number },
): string {
  const svg = createC1RoomPlanSvg(diagram);
  if (renderedSize) {
    svg.setAttribute("width", String(renderedSize.width));
    svg.setAttribute("height", String(renderedSize.height));
  }
  const markup = new XMLSerializer().serializeToString(svg);
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(markup)}`;
}

async function drawGroup(
  document: jsPDF,
  group: C1_RoomPlanPdfGroup,
  header: PdfDocumentHeader,
  imageOptimizer: C1_PdfImageOptimizer,
  showTags: boolean,
  onDiagramRendered?: () => Promise<void>,
) {
  drawHeader(document, group, header, showTags);
  const pageWidth = document.internal.pageSize.getWidth();
  const pageHeight = document.internal.pageSize.getHeight();
  const contentWidth = pageWidth - margin * 2;
  const contentHeight = pageHeight - contentTop - 62;
  const count = group.diagrams.length;
  const columns = count <= 2 ? 1 : 2;
  const rows = Math.ceil(count / columns);
  const gap = 14;
  const cellWidth = (contentWidth - gap * (columns - 1)) / columns;
  const cellHeight = (contentHeight - gap * (rows - 1)) / rows;

  for (let index = 0; index < count; index += 1) {
    const diagram = group.diagrams[index];
    const column = index % columns;
    const row = Math.floor(index / columns);
    const cellX = margin + column * (cellWidth + gap);
    const cellY = contentTop + row * (cellHeight + gap);
    const labelHeight = count > 1 ? 18 : 0;
    if (count > 1) {
      document.setFont("helvetica", "normal");
      document.setFontSize(7);
      document.setTextColor(mutedColor);
      document.text(`${diagram.floorLabel} · ${diagram.roomLabel}`, cellX, cellY + 8);
    }
    const availableHeight = cellHeight - labelHeight;
    const scale = Math.min(cellWidth / diagram.crop.width, availableHeight / diagram.crop.height);
    const width = diagram.crop.width * scale;
    const height = diagram.crop.height * scale;
    const drawingX = cellX + (cellWidth - width) / 2;
    const drawingY = cellY + labelHeight + (availableHeight - height) / 2;
    const svg = createC1RoomPlanSvg(diagram);
    await withDiagramTimeout((async () => {
      await imageOptimizer.optimize(svg, width, height);
      await document.svg(svg, {
        x: drawingX,
        y: drawingY,
        width,
        height,
      });
    })(), diagram);
    await onDiagramRendered?.();
  }
}

export async function downloadC1RoomPlanPdf(
  options: C1_RoomPlanPdfOptions,
  download = true,
  chapterOnly = false,
  onProgress?: (progress: number, message: string) => void,
): Promise<C1_PdfChapterArtifact> {
  onProgress?.(2, itx("c1.ui.Raumgrundriss_PDF_wird_vorbereitet", "Raumgrundriss-PDF wird vorbereitet …"));
  const document = new jsPDF({ orientation: "portrait", unit: "pt", format: "a4", compress: true });
  const imageOptimizer = new C1_PdfImageOptimizer();
  await registerC1PdfFonts(document);
  document.setProperties({ title: `ESTADA ${C1_PRODUCT_NAME} ${itx("c1.ui.Raumgrundrisse", "Raumgrundrisse")}`, author: "ESTADA" });
  const header = await loadPdfDocumentHeader();
  const floorEntries = Array.from(new Set(options.groups.map((group) => group.floorLabel))).flatMap((floorLabel) => {
    const firstPage = options.groups.findIndex((group) => group.floorLabel === floorLabel);
    return [
      { title: floorLabel, contentPageIndex: firstPage, level: 1 as const },
      ...options.groups.flatMap((group, index) =>
        group.floorLabel === floorLabel ? [{ title: roomPlanGroupTitle(group, options.showTags ?? true), contentPageIndex: index, level: 2 as const }] : [],
      ),
    ];
  });
  const tableOfContents = await createC1PdfTableOfContentsBlob({
    projectName: options.projectName,
    entries: floorEntries,
    pageSize: "A4",
    orientation: "portrait",
  });
  onProgress?.(8, itx("c1.ui.Raumgrundrisse_werden_gezeichnet", "Raumgrundrisse werden gezeichnet …"));
  await yieldToBrowser();
  const diagramCount = options.groups.reduce((sum, group) => sum + group.diagrams.length, 0);
  let renderedDiagrams = 0;
  for (let index = 0; index < options.groups.length; index += 1) {
    if (index > 0) document.addPage("a4", "portrait");
    await drawGroup(document, options.groups[index], header, imageOptimizer, options.showTags ?? true, async () => {
      renderedDiagrams += 1;
      onProgress?.(
        8 + Math.round((renderedDiagrams / Math.max(diagramCount, 1)) * 72),
        itx("c1.ui.Raumgrundrisse_werden_gezeichnet_value1_value2", "Raumgrundrisse werden gezeichnet ({{value1}}/{{value2}}) …", { "{{value1}}": String(renderedDiagrams), "{{value2}}": String(diagramCount) }),
      );
      await yieldToBrowser();
    });
    drawFooter(
      document,
      options,
      index + 3 + tableOfContents.pageCount,
      options.groups.length + 2 + tableOfContents.pageCount,
    );
  }

  const chapterPages = options.groups.map((group, pageIndex) => ({
    title: roomPlanGroupTitle(group, options.showTags ?? true),
    sectionTitle: group.floorLabel,
    pageIndex,
    sourceIds: Array.from(new Set(group.diagrams.flatMap((diagram) => diagram.roomIds))),
  }));
  const contentBlob = await protectPdfOutput(document.output("blob"), "plan");
  if (chapterOnly) {
    onProgress?.(100, itx("c1.ui.Raumgrundrisse_sind_fertig", "Raumgrundrisse sind fertig."));
    return { blob: contentBlob, pages: chapterPages };
  }

  onProgress?.(84, itx("c1.ui.PDF_Seiten_werden_erganzt", "PDF-Seiten werden ergänzt …"));
  await yieldToBrowser();
  const coverBlob = await createC1MarkdownCoverPdfBlob({
    documentName: itx("c1.ui.Raumgrundrisse", "Raumgrundrisse"),
    projectName: options.projectName,
    dateText: options.dateText,
    logo: ESTADA_PDF_COVER_LOGO,
    pageSize: "A4",
    orientation: "portrait",
  });
  const documentationBlob = await createC1MarkdownCoverPdfBlob({
    documentName: itx("c1.ui.Raumgrundrisse", "Raumgrundrisse"),
    markdownTemplate: getC1DocumentationMarkdown(),
    textAlign: "left",
    projectName: options.projectName,
    dateText: options.dateText,
    logo: C1_DOCUMENT_LOGO,
    pageSize: "A4",
    orientation: "portrait",
  });
  const merged = await PDFDocument.create();
  onProgress?.(90, itx("c1.ui.PDF_Seiten_werden_zusammengefuhrt", "PDF-Seiten werden zusammengeführt …"));
  await yieldToBrowser();
  for (const source of [coverBlob, documentationBlob, tableOfContents.blob]) {
    const sourceDocument = await PDFDocument.load(await source.arrayBuffer());
    const sourcePages = await merged.copyPages(sourceDocument, sourceDocument.getPageIndices());
    sourcePages.forEach((page) => merged.addPage(page));
  }
  const contentDocument = await PDFDocument.load(await contentBlob.arrayBuffer());
  const pages = await merged.copyPages(contentDocument, contentDocument.getPageIndices());
  pages.forEach((page) => merged.addPage(page));
  addC1PdfOutlines(merged, [
    { title: itx("c1.ui.Deckblatt", "Deckblatt"), pageIndex: 0 },
    { title: itx("c1.ui.Dokumentation", "Dokumentation"), pageIndex: 1 },
    { title: itx("pageDocumentType.coverPage", "Inhaltsverzeichnis"), pageIndex: 2 },
    ...Array.from(new Set(options.groups.map((group) => group.floorLabel))).map((floorLabel) => {
      const floorGroups = options.groups
        .map((group, index) => ({ group, index }))
        .filter(({ group }) => group.floorLabel === floorLabel);
      return {
        title: floorLabel,
        pageIndex: floorGroups[0].index + 2 + tableOfContents.pageCount,
        children: floorGroups.map(({ group, index }) => ({
          title: roomPlanGroupTitle(group, options.showTags ?? true),
          pageIndex: index + 2 + tableOfContents.pageCount,
        })),
      };
    }),
  ]);
  addC1PdfPageLinks(
    merged,
    tableOfContents.links.map((link) => ({
      ...link,
      sourcePageIndex: 2 + link.tableOfContentsPageIndex,
      targetPageIndex: 2 + tableOfContents.pageCount + link.contentPageIndex,
    })),
  );
  await deduplicateC1PdfImages(merged);
  const bytes = await merged.save();
  const blob = new Blob([Uint8Array.from(bytes)], { type: "application/pdf" });
  if (download) {
    const url = URL.createObjectURL(blob);
    const link = window.document.createElement("a");
    link.href = url;
    link.download = c1PdfFileName(options.projectName);
    link.click();
    window.setTimeout(() => URL.revokeObjectURL(url), 1_000);
  }
  onProgress?.(100, itx("c1.ui.Raumgrundriss_PDF_ist_fertig", "Raumgrundriss-PDF ist fertig."));
  return { blob, pages: chapterPages };
}
